import type { YougovMonthlyRow } from './yougovData.schema';

/** Sentinel for the "All regions" option; never a real YouGov region code. */
// eslint-disable-next-line i18next/no-literal-string -- sentinel value, not copy
export const ALL_REGIONS = 'all';

export interface MarketingPerformanceFilters {
  /** Inclusive month bound, `YYYY-MM-01`. */
  from: string;
  /** Inclusive month bound, `YYYY-MM-01`. */
  to: string;
  /** A region code, or `ALL_REGIONS`. */
  region: string;
}

export function availableMonths(rows: YougovMonthlyRow[]): string[] {
  return Array.from(new Set(rows.map((r) => r.month))).sort();
}

export function filterRows(
  rows: YougovMonthlyRow[],
  filters: MarketingPerformanceFilters,
): YougovMonthlyRow[] {
  // Months are `YYYY-MM-01`, so string comparison is chronological and needs no Date.
  const lo = filters.from <= filters.to ? filters.from : filters.to;
  const hi = filters.from <= filters.to ? filters.to : filters.from;
  return rows.filter(
    (r) =>
      r.month >= lo &&
      r.month <= hi &&
      (filters.region === ALL_REGIONS || r.region === filters.region),
  );
}

/**
 * Unweighted mean of the non-null scores. YouGov's per-day respondent base was
 * deliberately dropped from the stored blob, so weighting is not possible here;
 * measured impact on the reference export is ~0.09pt. Returns null rather than 0
 * when nothing is measurable, so callers can render a dash instead of a real zero.
 */
export function meanScore(rows: YougovMonthlyRow[]): number | null {
  let sum = 0;
  let count = 0;
  for (const r of rows) {
    if (r.score !== null) {
      sum += r.score;
      count += 1;
    }
  }
  return count > 0 ? sum / count : null;
}
